// hooks/useSidebar.js
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { 
  toggleSidebar, 
  setSidebarCollapsed, 
  collapseSidebar, 
  expandSidebar 
} from "../slices/commonReducer";

/**
 * Custom hook to manage sidebar state across the application
 * Syncs React state with CSS framework data attributes
 */
const useSidebar = () => {
    const dispatch = useDispatch();
    
    // Get the current sidebar collapsed state from Redux store
    const { isSidebarCollapsed } = useSelector(state => state.common);

    /**
     * Apply CSS framework data attributes to body
     * @param {boolean} collapsed - Whether sidebar should be collapsed
     */
    const applySidebarCSS = (collapsed) => {
        const body = document.body;
        if (collapsed) {
            // Set the data attribute that your CSS framework expects for collapsed state
            body.setAttribute('data-sidebar-size', 'sm');
        } else {
            // Remove the attribute to show full sidebar
            body.removeAttribute('data-sidebar-size');
            // Or set to normal size if your framework expects it
            // body.setAttribute('data-sidebar-size', 'lg');
        }
    };

    /**
     * Toggle sidebar between collapsed and expanded state
     */
    const toggleSidebarState = () => {
        const newState = !isSidebarCollapsed;
        dispatch(toggleSidebar());
        applySidebarCSS(newState);
    };

    /**
     * Force collapse the sidebar (set to true)
     * Useful when opening modals or when you need more screen space
     */
    const collapseSidebarState = () => {
        dispatch(collapseSidebar());
        applySidebarCSS(true);
    };

    /**
     * Force expand the sidebar (set to false)
     * Useful when closing modals or when sidebar needs to be visible
     */
    const expandSidebarState = () => {
        dispatch(expandSidebar());
        applySidebarCSS(false);
    };

    /**
     * Set sidebar to a specific state (true for collapsed, false for expanded)
     * @param {boolean} collapsed - Whether sidebar should be collapsed
     */
    const setSidebarState = (collapsed) => {
        dispatch(setSidebarCollapsed(collapsed));
        applySidebarCSS(collapsed);
    };

    // Sync initial state on component mount
    React.useEffect(() => {
        applySidebarCSS(isSidebarCollapsed);
    }, []);

    // Return all the sidebar state and control functions
    return {
        isSidebarCollapsed,           // Current state of sidebar (true = collapsed, false = expanded)
        toggleSidebarState,           // Function to toggle sidebar state
        collapseSidebarState,         // Function to force collapse sidebar
        expandSidebarState,           // Function to force expand sidebar
        setSidebarState,              // Function to set sidebar to specific state
        
        // Alias functions for better readability in components
        toggle: toggleSidebarState,
        collapse: collapseSidebarState,
        expand: expandSidebarState,
        setCollapsed: setSidebarState
    };
};

export default useSidebar;